import { useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, FileSpreadsheet, UploadCloud } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/use-toast'
import { useBulkUpload, useOrganizations } from '@/features/workers/hooks'
import type { BulkUploadResponse } from '@/features/workers/types'
import { cn } from '@/lib/utils'
import { ApiError } from '@/lib/api'

export default function BulkUploadPage() {
  const { show } = useToast()
  const { data: orgsData, isLoading: loadingOrgs } = useOrganizations()
  const organizations = orgsData?.items ?? []

  const [file, setFile] = useState<File | null>(null)
  const [organizationId, setOrganizationId] = useState<string>('')
  const [dragOver, setDragOver] = useState(false)
  const [result, setResult] = useState<BulkUploadResponse | null>(null)
  const [fatalError, setFatalError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const upload = useBulkUpload()

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const picked = files[0]
    if (!picked.name.toLowerCase().endsWith('.xlsx')) {
      show({
        kind: 'error',
        title: 'Archivo inválido',
        description: 'Solo se aceptan archivos .xlsx',
      })
      return
    }
    setFile(picked)
    setResult(null)
    setFatalError(null)
  }

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  const submit = async () => {
    if (!file || !organizationId) return
    setFatalError(null)
    setResult(null)
    try {
      const res = await upload.mutateAsync({ file, organizationId })
      setResult(res)
      show({
        kind: res.errors.length > 0 ? 'warning' : 'success',
        title: `${res.inserted} trabajadores cargados`,
        description:
          res.skipped > 0
            ? `${res.skipped} filas fueron omitidas. Revisa el detalle abajo.`
            : 'Todos los registros se insertaron sin errores.',
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const code = typeof err.body?.error === 'string' ? err.body.error : 'server_error'
        const msg =
          code === 'invalid_file'
            ? 'El archivo parece estar corrupto o no es un .xlsx válido.'
            : code === 'empty_file'
              ? 'El archivo no contiene filas de datos.'
              : code === 'missing_columns'
                ? `Faltan columnas obligatorias: ${(err.body?.missing as string[] | undefined)?.join(', ') ?? 'rut, nombres, apellidos'}`
                : 'No se pudo procesar el archivo.'
        setFatalError(msg)
      } else {
        setFatalError('Error de conexión con el servidor.')
      }
    }
  }

  const reset = () => {
    setFile(null)
    setResult(null)
    setFatalError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const orgOptions = organizations.map((o) => ({ value: o.id, label: o.name }))

  return (
    <div className="flex flex-col gap-5" data-testid="bulk-upload-page">
      <div>
        <Link
          to="/app/workers"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Volver a trabajadores
        </Link>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
          Carga masiva de trabajadores
        </h1>
        <p className="text-sm text-slate-600">
          Sube un archivo Excel (.xlsx) con las columnas{' '}
          <code>rut, nombres, apellidos, fecha_nacimiento</code>. Los RUT inválidos o duplicados se
          reportarán por fila sin bloquear el resto.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Selecciona la empresa contratante</CardTitle>
          <CardDescription>
            Todos los trabajadores del archivo quedarán asociados a esta empresa.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Select
              value={organizationId}
              onChange={setOrganizationId}
              options={orgOptions}
              placeholder={loadingOrgs ? 'Cargando empresas…' : 'Elegir empresa contratante'}
              data-testid="bulk-upload-org"
              disabled={loadingOrgs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. Sube el archivo Excel</CardTitle>
          <CardDescription>
            Arrastra el archivo sobre la zona o haz clic para seleccionarlo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label
            htmlFor="bulk-file"
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors',
              dragOver
                ? 'border-[color:var(--color-fitlab-blue)] bg-[color:var(--color-fitlab-blue)]/5'
                : 'border-slate-300 hover:border-slate-400',
            )}
            data-testid="bulk-upload-dropzone"
          >
            {file ? (
              <>
                <FileSpreadsheet
                  className="h-10 w-10 text-[color:var(--color-fitlab-green)]"
                  aria-hidden
                />
                <p className="text-sm font-medium text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500">
                  {(file.size / 1024).toFixed(1)} KB — click para cambiar
                </p>
              </>
            ) : (
              <>
                <UploadCloud className="h-10 w-10 text-slate-400" aria-hidden />
                <p className="text-sm font-medium text-slate-700">Arrastra tu archivo .xlsx aquí</p>
                <p className="text-xs text-slate-500">o haz clic para buscarlo en tu equipo</p>
              </>
            )}
            <input
              ref={inputRef}
              id="bulk-file"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => handleFiles(e.target.files)}
              className="sr-only"
              data-testid="bulk-upload-input"
            />
          </label>

          <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
            {file && (
              <Button variant="ghost" onClick={reset}>
                Quitar archivo
              </Button>
            )}
            <Button
              onClick={submit}
              disabled={!file || !organizationId}
              loading={upload.isPending}
              data-testid="bulk-upload-submit"
              leftIcon={<UploadCloud className="h-4 w-4" />}
            >
              Confirmar carga
            </Button>
          </div>

          {upload.isPending && (
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-fitlab-gradient" />
            </div>
          )}

          {fatalError && (
            <div
              role="alert"
              className="mt-4 rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 px-3 py-2 text-sm text-[color:var(--color-fitlab-red-hover)]"
            >
              {fatalError}
            </div>
          )}
        </CardContent>
      </Card>

      {result && <ResultSummary result={result} />}
    </div>
  )
}

function ResultSummary({ result }: { result: BulkUploadResponse }) {
  const invalid = result.errors.filter((e) => e.reason === 'invalid_rut')
  const dupes = result.errors.filter(
    (e) => e.reason === 'duplicate_rut' || e.reason === 'duplicate_in_organization',
  )
  const other = result.errors.filter(
    (e) => !['invalid_rut', 'duplicate_rut', 'duplicate_in_organization'].includes(e.reason),
  )

  return (
    <Card data-testid="bulk-upload-result">
      <CardHeader>
        <CardTitle>Resultado de la carga</CardTitle>
        <CardDescription>
          Detalle fila por fila. Los errores no bloquean la inserción del resto de registros.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard label="Insertados" value={result.inserted} tone="success" />
          <StatCard
            label="Omitidos"
            value={result.skipped}
            tone={result.skipped ? 'warning' : 'neutral'}
          />
          <StatCard
            label="Errores"
            value={result.errors.length}
            tone={result.errors.length ? 'danger' : 'neutral'}
          />
        </div>

        {invalid.length > 0 && (
          <ErrorSection title="RUT inválidos" tone="danger">
            <ul className="space-y-1 text-sm text-slate-700">
              {invalid.slice(0, 20).map((e, idx) => (
                <li key={`${e.row}-${idx}`}>
                  Fila {e.row}: <span className="font-mono text-xs">{e.rut ?? '—'}</span>
                </li>
              ))}
              {invalid.length > 20 && (
                <li className="text-xs text-slate-500">+ {invalid.length - 20} más…</li>
              )}
            </ul>
          </ErrorSection>
        )}

        {dupes.length > 0 && (
          <ErrorSection title="RUT duplicados" tone="warning">
            <ul className="space-y-1 text-sm text-slate-700">
              {dupes.slice(0, 20).map((e, idx) => (
                <li key={`${e.row}-${idx}`}>
                  Fila {e.row}: <span className="font-mono text-xs">{e.rut ?? '—'}</span>
                  {e.existingOrganizationName && (
                    <span className="text-xs text-slate-500">
                      {' '}
                      — ya existe en {e.existingOrganizationName}
                    </span>
                  )}
                </li>
              ))}
              {dupes.length > 20 && (
                <li className="text-xs text-slate-500">+ {dupes.length - 20} más…</li>
              )}
            </ul>
          </ErrorSection>
        )}

        {other.length > 0 && (
          <ErrorSection title="Otros errores" tone="neutral">
            <ul className="space-y-1 text-sm text-slate-700">
              {other.slice(0, 20).map((e, idx) => (
                <li key={`${e.row}-${idx}`}>
                  Fila {e.row}: {e.reason}
                </li>
              ))}
            </ul>
          </ErrorSection>
        )}
      </CardContent>
    </Card>
  )
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'success' | 'warning' | 'danger' | 'neutral'
}) {
  const toneClass = {
    success: 'border-[color:var(--color-fitlab-green)]/30 bg-[color:var(--color-fitlab-green)]/5',
    warning: 'border-amber-300 bg-amber-50',
    danger: 'border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5',
    neutral: 'border-slate-200 bg-slate-50',
  }[tone]
  return (
    <div className={cn('rounded-md border p-3', toneClass)}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    </div>
  )
}

function ErrorSection({
  title,
  tone,
  children,
}: {
  title: string
  tone: 'danger' | 'warning' | 'neutral'
  children: React.ReactNode
}) {
  return (
    <section className="mt-5">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Badge tone={tone === 'danger' ? 'danger' : tone === 'warning' ? 'warning' : 'neutral'}>
          {title}
        </Badge>
      </h3>
      {children}
    </section>
  )
}
